import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import { api } from '../../services/api';
import Swal from 'sweetalert2';
import '../Aeflor/convenio.css';

// URLs e Constantes
const IMAGE_BASE_URL = '/usuario/notificacao/imagem';
const NOTIFICACAO_BASE_URL = '/usuario/notificacao';
const NOTIFICACAO_TOTAL_URL = '/usuario/notificacoes';
const MESSAGES_PER_PAGE = 20;

// Função para formatar data para o POST (dd/mm/yyyy) usando métodos UTC
const formatDateToDDMMYYYY = (isoDate) => {
    if (!isoDate) return '';
    try {
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return '';

        // USANDO MÉTODOS UTC para evitar o fuso horário
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();

        return `${day}/${month}/${year}`;
    } catch (e) {
        return '';
    }
};

const Notificacoes = () => {
    const chapa = localStorage.getItem("chapa");
    const [messages, setMessages] = useState([]);
    const [imageMap, setImageMap] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalMessages, setTotalMessages] = useState(0);
    const [loading, setLoading] = useState(false);

    const [selectedIndex, setSelectedIndex] = useState(null);
    const [imageZoomed, setImageZoomed] = useState(null);

    const totalPages = Math.ceil(totalMessages / MESSAGES_PER_PAGE);

    const selectedMessage = useMemo(() => {
        return selectedIndex !== null ? messages[selectedIndex] : null;
    }, [selectedIndex, messages]);

    // Busca de imagem (Estável)
    const fetchImage = useCallback(async (codImagem) => {
        const imagemKey = codImagem || 'default';

        if (!chapa) return;

        try {
            const res = await api.get(`${IMAGE_BASE_URL}?usuario=${chapa}&codigo=${codImagem}`);
            const imageDataUri = `data:image/jpeg;base64,${res.data}`;

            setImageMap(prevMap => {
                if (prevMap[imagemKey] && prevMap[imagemKey] !== 'error') return prevMap;

                return {
                    ...prevMap,
                    [imagemKey]: imageDataUri
                };
            });
        } catch (err) {
            console.error(`Erro ao carregar imagem ${imagemKey}`, err);
            setImageMap(prevMap => ({
                ...prevMap,
                [imagemKey]: 'error'
            }));
        }
    }, [chapa]);

    // Busca o total de mensagens (Estável)
    const fetchTotalMessages = useCallback(async () => {
        if (!chapa) return;
        try {
            const res = await api.get(`${NOTIFICACAO_TOTAL_URL}?usuario=${chapa}`);
            if (res.data && typeof res.data.total === 'number') {
                setTotalMessages(res.data.total);
            } else {
                setTotalMessages(0);
            }
        } catch (err) {
            console.error("Erro ao carregar total de notificações", err);
            setTotalMessages(0);
        }
    }, [chapa]);

    // Busca das notificações por página (Estável)
    const fetchMessages = useCallback(async (pageNumber) => {
        if (!chapa) return;

        setLoading(true);
        try {
            const res = await api.get(`${NOTIFICACAO_BASE_URL}?usuario=${chapa}&pagina=${pageNumber}`);

            const newMessages = Array.isArray(res.data) ? res.data : [];

            if (newMessages.length > 0) {
                setMessages(newMessages);

                newMessages.forEach(msg => {
                    if (msg.imagem) {
                        fetchImage(msg.imagem);
                    }
                });
            } else {
                setMessages([]);
            }
        } catch (err) {
            console.error("Erro ao carregar notificações", err);
            Swal.fire("Erro", "Não foi possível carregar as notificações.", "error");
            setMessages([]);
        } finally {
            setLoading(false);
        }
    }, [chapa, fetchImage]);

    // Efeitos
    useEffect(() => {
        fetchTotalMessages();
    }, [fetchTotalMessages]);

    // Este useEffect chama fetchMessages de forma estável
    useEffect(() => {
        if (chapa && currentPage > 0) {
            fetchMessages(currentPage);
        }
    }, [chapa, currentPage, fetchMessages]);


    // Função de mudança de página (Mantida)
    const handlePageChange = (pageNumber) => {
        if (pageNumber !== currentPage && pageNumber > 0 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
            setSelectedIndex(null);
        }
    };

    // Função de abertura e leitura do Modal Principal
    const handleReadAndOpen = useCallback(async (index, message) => {
        setSelectedIndex(index);

if (message.status === 'E') {
            try {
                const formattedDate = formatDateToDDMMYYYY(message.datamsg);

                await api.post(
                    `${NOTIFICACAO_BASE_URL}?usuario=${chapa}&codigo=${message.codmsg}&tipo=${message.tipo}&datamsg=${formattedDate}`
                );
                
                // 1. Atualiza o state local (marca a mensagem como lida 'L')
                setMessages(prevMessages =>
                    prevMessages.map(msg =>
                        msg.codmsg === message.codmsg ? { ...msg, status: 'L' } : msg
                    )
                );

                // 2. Diminui o contador de mensagens não lidas no localStorage
                const currentMsgCount = parseInt(localStorage.getItem("msg") || "0", 10);
                if (currentMsgCount > 0) {
                    const newMsgCount = currentMsgCount - 1;
                    localStorage.setItem("msg", String(newMsgCount));
                }

            } catch (err) {
                console.error("Erro ao marcar mensagem como lida", err);
            }
        }
    }, [chapa]);

    // Função para abrir a imagem em tela cheia
    const handleImageZoom = useCallback((e, imageSource) => {
        e.stopPropagation();

        if (imageSource && imageSource !== 'error') {
            setImageZoomed(imageSource);
        }
    }, []);

    // Função de fechamento do Modal Principal
    const handleCloseModal = () => {
        setSelectedIndex(null);
    };

    // Funções auxiliares (Mantidas)
    const goToNextMessage = () => {
        if (selectedIndex !== null && selectedIndex < messages.length - 1) {
            const nextIndex = selectedIndex + 1;
            handleReadAndOpen(nextIndex, messages[nextIndex]);
        }
    };

    const goToPrevMessage = () => {
        if (selectedIndex !== null && selectedIndex > 0) {
            const prevIndex = selectedIndex - 1;
            handleReadAndOpen(prevIndex, messages[prevIndex]);
        }
    };

    const formatDate = (isoDate) => {
        if (!isoDate) return 'N/A';
        const date = new Date(isoDate);
        return date.toLocaleDateString('pt-BR');
    };

    const truncateMessage = (text, maxLength = 100) => {
        if (!text) return '';
        const cleanedText = text.replace(/\\n/g, ' ').replace(/\s\s+/g, ' ');
        return cleanedText.length > maxLength ? cleanedText.substring(0, maxLength) + '...' : cleanedText;
    };


    const PaginationControls = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        return (
            <nav className="d-flex justify-content-center mt-4">
                <ul className="pagination">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                        >
                            Anterior
                        </button>
                    </li>

                    {pages.map(number => (
                        <li key={`page-${number}`} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => handlePageChange(number)}
                                disabled={loading}
                            >
                                {number}
                            </button>
                        </li>
                    ))}

                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || loading}
                        >
                            Próximo
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    const MessageList = () => (
        <div className={`list-group ${loading && messages.length > 0 ? 'opacity-50' : ''}`}>
            {messages.map((message, index) => {
                const isUnread = message.status === 'E';
                const imageSource = message.imagem ? imageMap[message.imagem] : null;
                const uniqueKey = `${message.codmsg || 'no-id'}-${message.linha || index}`;

                return (
                    <div
                        key={uniqueKey}
                        className={`list-group-item list-group-item-action ${isUnread ? 'bg-info bg-opacity-10' : ''}`}
                        onClick={() => handleReadAndOpen(index, message)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start' }}
                    >
                        {/* Imagem/Ícone */}
                        <div style={{ marginRight: '15px', flexShrink: 0 }}>
                            {imageSource && imageSource !== 'error' ? (
                                <img
                                    src={imageSource}
                                    alt="Notificação"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '50%', border: isUnread ? '2px solid #0d6efd' : 'none' }}
                                />
                            ) : (
                                <div style={{ width: '50px', height: '50px', backgroundColor: isUnread ? '#dbeeff' : '#f0f0f0', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '10px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {isUnread ? 'NOVA' : 'LIDA'}
                                </div>
                            )}
                        </div>

                        {/* Conteúdo Principal (Título, Data e Resumo) */}
                        <div style={{ flexGrow: 1 }}>
                            <div className="d-flex w-100 justify-content-between align-items-start">
                                <h5 className={`mb-1 ${isUnread ? 'text-primary' : 'text-dark'}`}>{message.assunto}</h5>
                                <small className={`ms-3 ${isUnread ? 'text-danger fw-bold' : 'text-muted'}`}>
                                    {formatDate(message.datamsg)}
                                </small>
                            </div>
                            <p className={`mb-1 ${isUnread ? 'text-dark fw-bold' : 'text-muted'}`}>
                                {truncateMessage(message.mensagem)}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    // NOVO COMPONENTE: Modal de Zoom da Imagem
    // ... (código anterior omitido por brevidade)

    // NOVO COMPONENTE: Modal de Zoom da Imagem (AJUSTADO)
    const ImageZoomModal = ({ imageSource, onClose }) => {
        if (!imageSource) return null;

        return (
            <div
                className={`modal fade show d-block`}
                tabIndex="-1"
                role="dialog"
                // Estilo para centralizar e ocupar a tela
                style={{ backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 2000 }}
                onClick={onClose}
            >
                <div
                    // CORREÇÃO: Removido modal-dialog-centered. Adicionado h-100 para forçar altura total.
                    className="modal-dialog modal-fullscreen-sm-down modal-xl h-100"
                    role="document"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* CORREÇÃO: Adicionado h-100 ao modal-content */}
                    <div className="modal-content bg-transparent border-0 h-100">
                        <div className="modal-header border-0 p-2 d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn-close btn-close-white"
                                aria-label="Fechar"
                                onClick={onClose}
                            ></button>
                        </div>

                        {/* Mantido: d-flex align-items-center justify-content-center para centralização da imagem */}
                        <div className="modal-body p-0 d-flex align-items-center justify-content-center">
                            <img
                                src={imageSource}
                                alt="Imagem em tela cheia"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%', // Agora pode usar 100% da altura do body
                                    objectFit: 'contain'
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    

    // COMPONENTE Modal de Visualização com Navegação (Mantido)
    const MessageModal = () => {
        if (!selectedMessage) return null;

        const imageSource = selectedMessage.imagem ? imageMap[selectedMessage.imagem] : null;
        const isFirst = selectedIndex === 0;
        const isLast = selectedIndex === messages.length - 1;

        const handleBackdropClick = (e) => {
            if (e.target === e.currentTarget) {
                handleCloseModal();
            }
        };


        return (
            <div
                className={`modal fade show d-block`}
                tabIndex="-1"
                role="dialog"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto', paddingRight: '17px', zIndex: 1050 }}
                onClick={handleBackdropClick}
            >
                <div
                    className="modal-dialog modal-lg modal-dialog-centered"
                    role="document"
                >
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{selectedMessage.assunto}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Fechar"
                                onClick={handleCloseModal}
                            ></button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                            <small className="text-muted d-block mb-3">
                                Data: {formatDate(selectedMessage.datamsg)}
                            </small>

                            {imageSource && imageSource !== 'error' && (
                                <div
                                    className="text-center mb-3"
                                    onClick={(e) => handleImageZoom(e, imageSource)}
                                    style={{ cursor: 'zoom-in' }}
                                >
                                    <img
                                        src={imageSource}
                                        alt={selectedMessage.assunto}
                                        style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                                    />
                                    <small className="d-block mt-1 text-muted">Clique para ampliar</small>
                                </div>
                            )}

                            <div className="message-content">
                                {selectedMessage.mensagem.split('\n').map((line, i) => (
                                    <p key={i}>{line}</p>
                                ))}
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between" style={{ flexWrap: 'nowrap' }}>
                            <button
                                type="button"
                                className="btn btn-secondary flex-shrink-0"
                                onClick={goToPrevMessage}
                                disabled={isFirst}
                            >
                                ← Anterior
                            </button>

                            <button
                                type="button"
                                className="btn btn-outline-secondary flex-shrink-0 mx-2"
                                onClick={handleCloseModal}
                            >
                                Fechar
                            </button>

                            <button
                                type="button"
                                className="btn btn-primary flex-shrink-0"
                                onClick={goToNextMessage}
                                disabled={isLast}
                            >
                                Próximo →
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Estrutura Principal de Renderização (Mantida)
    return (
        <div>
            <Menu modulo="ARBOR" />

            <div className="container mt-4 mb-5">
                <div className="row justify-content-center">
                    <div className="col-12 col-lg-8">
                        <h3>Minhas Notificações ({totalMessages} Total)</h3>

                        {/* Loading/Empty/Success States */}
                        {loading && currentPage === 1 && messages.length === 0 && (
                            <div className="text-center my-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Carregando...</span>
                                </div>
                                <p className="mt-2 text-primary">Carregando notificações...</p>
                            </div>
                        )}
                        {messages.length === 0 && !loading && (
                            <p className="text-center text-muted">Você não tem notificações.</p>
                        )}
                        {messages.length > 0 && (
                            <>
                                <MessageList />
                                {loading && currentPage > 1 && (
                                    <div className="text-center my-2">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Buscando...</span>
                                        </div>
                                        <p className="mt-2 text-primary small">Buscando mensagens da página {currentPage}...</p>
                                    </div>
                                )}
                                <PaginationControls />
                            </>
                        )}
                    </div>
                </div>
            </div>
            <Footer />

            {/* Renderiza o Modal de Leitura */}
            {selectedMessage && <MessageModal />}

            {/* Renderiza o Modal de Zoom CORRIGIDO */}
            {imageZoomed && <ImageZoomModal imageSource={imageZoomed} onClose={() => setImageZoomed(null)} />}
        </div>
    );
};

export default Notificacoes;